import React, { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import { DataGrid } from '@orijinworks/frontend-commons';
import { ComingSoonModal } from '../../../../shared/components';
import { NavyMainTypography } from '../../../Feeds/Feeds.style';
import { formatLastRefreshedText } from '../../utils/reentry-planning.util';
import { useReentryPlansTableHook } from './useReentryPlansTable';
import { DataGridWrapper } from './ReentryPlansTable.styles';
import { REENTRY_PLANS_PAGE_SIZE_OPTIONS } from '../../constants/reentry-planning.constants';

const buildColumns = (locationOptions = []) => [
  {
    name: 'LearnerCell',
    preset: 'LearnerCell',
    filter: {
      type: 'LearnerCell',
    },
    displayName: 'Resident',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'preDefined',
    sortByProp: 'learnerName',
  },

  {
    name: 'location',
    displayName: 'Locations',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: false,
    isVisible: true,
    hidden: true,
    type: 'value',
    filter: {
      type: 'MultiTagCell',
    },
    options: locationOptions,
  },
  {
    name: 'status',
    displayName: 'Plan Status',
    isSortable: false,
    isFilterable: false,
    hidden : true,
    type: 'value',
    isVisible: false,
    sortByProp: 'status',
  },
  {
    name: 'editors',
    displayName: 'Editors',
    isSortable: false,
    isFilterable: false,
    type: 'value',
    hidden : true,
    isVisible: false,
  },
  {
    name: 'action',
    displayName: 'Action',
    type: 'override',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
  },
];

const ReentryPlansTable = ({ lastRefreshed, onRefreshHandler }) => {
  const {
    plans,
    totalCount,
    isLoading,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onFilterChange,
    reloadPlans,
    locationFilterOptions,
    isComingSoonModalOpen,
    handleCloseComingSoonModal,
    handleOpenComingSoonModal,
  } = useReentryPlansTableHook({ refreshSignal: lastRefreshed });

  const columns = useMemo(
    () => buildColumns(locationFilterOptions),
    [locationFilterOptions],
  );

  const handleRefresh = () => {
    onRefreshHandler?.();
    reloadPlans();
  };

  return (
    <Stack
      height="100%"
      minWidth={0}
      overflow="hidden"
      backgroundColor="white"
      direction="column"
      spacing={2}
      boxSizing="border-box"
      padding="20px"
    >
      <Stack spacing={0.5} flexShrink={0}>
        <NavyMainTypography variant="h1">All Plans</NavyMainTypography>
        <Typography variant="body2" color="text.secondary">
          {formatLastRefreshedText(lastRefreshed)}
        </Typography>
      </Stack>
      <DataGridWrapper>
        <DataGrid
          columns={columns}
          data={plans}
          pageSize={pageSize}
          pageSizeOptions={REENTRY_PLANS_PAGE_SIZE_OPTIONS}
          paginationMode="server"
          isLoading={isLoading}
          isAllSelected={false}
          showSelection={false}
          onSelectionChange={() => {}}
          serverSidePaginationProps={{
            onPageChange,
            onPageSizeChange,
            onSortChange,
            onFilterChange,
            totalRecordsCount: totalCount,
          }}
          onRefreshHandler={handleRefresh}
          primaryButtonProps={{
            text: 'Create New Plan',
            variant: 'contained',
            onClick: handleOpenComingSoonModal,
          }}
          tableSx={{
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            tableLayout: 'fixed',
            height: 'auto !important',
          }}
          tdStyles={{
            LearnerCell: {
              minWidth: 250,
              maxWidth: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
            action: {
              width: 140,
              minWidth: 140,
              maxWidth: 140,
              textAlign: 'center',
            },
          }}
        />
        <ComingSoonModal
          open={isComingSoonModalOpen}
          onClose={handleCloseComingSoonModal}
          message="Plan details will be available in a future release."
        />
      </DataGridWrapper>
    </Stack>
  );
};

export default ReentryPlansTable;
